import { useCallback, useEffect, useMemo, useState } from 'react';
import type { LeadCampaignRoutePublic } from '@workspace/types';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { leadsApi, type CampaignAnalyticsRow } from '../api/leadsApi';
import { contactAgentError } from '../../contact-agent/api/contactAgentScope';

type Props = {
  scopeKey: string;
  availableRoutes: Array<{ key: string; vertical: string }>;
};

type Draft = {
  source: string;
  routeKey: string;
  externalCampaignId: string;
  externalGroupId: string;
  externalAdId: string;
  externalFormId: string;
  note: string;
  enabled: boolean;
};

const EMPTY: Draft = {
  source: 'META',
  routeKey: '',
  externalCampaignId: '',
  externalGroupId: '',
  externalAdId: '',
  externalFormId: '',
  note: '',
  enabled: true,
};

function payload(draft: Draft) {
  return {
    source: draft.source.trim().toUpperCase(),
    routeKey: draft.routeKey,
    externalCampaignId: draft.externalCampaignId.trim() || null,
    externalGroupId: draft.externalGroupId.trim() || null,
    externalAdId: draft.externalAdId.trim() || null,
    externalFormId: draft.externalFormId.trim() || null,
    note: draft.note.trim() || null,
    enabled: draft.enabled,
  };
}

export function CampaignRoutingCard({ scopeKey, availableRoutes }: Props) {
  const [rows, setRows] = useState<LeadCampaignRoutePublic[]>([]);
  const [analytics, setAnalytics] = useState<CampaignAnalyticsRow[]>([]);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [editingId, setEditingId] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const routeLabels = useMemo(
    () => new Map(availableRoutes.map(route => [route.key, route.vertical])),
    [availableRoutes],
  );

  const load = useCallback(async () => {
    try {
      const [routeRows, analyticsRows] = await Promise.all([
        leadsApi.listCampaignRoutes(),
        leadsApi.getCampaignAnalytics(),
      ]);
      setRows(routeRows);
      setAnalytics(analyticsRows);
    } catch (err) {
      setError(contactAgentError(err));
    }
  }, []);

  useEffect(() => {
    setDraft(EMPTY);
    setEditingId('');
    void load();
  }, [scopeKey, load]);

  const edit = (row: LeadCampaignRoutePublic) => {
    setEditingId(row.id);
    setDraft({
      source: row.source,
      routeKey: row.routeKey,
      externalCampaignId: row.externalCampaignId ?? '',
      externalGroupId: row.externalGroupId ?? '',
      externalAdId: row.externalAdId ?? '',
      externalFormId: row.externalFormId ?? '',
      note: row.note ?? '',
      enabled: row.enabled,
    });
  };

  const save = async () => {
    if (!draft.source.trim() || !draft.routeKey) {
      setError('Fuente y route son requeridos.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (editingId) {
        await leadsApi.updateCampaignRoute(editingId, payload(draft));
      } else {
        await leadsApi.createCampaignRoute(payload(draft));
      }
      setDraft(EMPTY);
      setEditingId('');
      await load();
    } catch (err) {
      setError(contactAgentError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    setError('');
    try {
      await leadsApi.deleteCampaignRoute(id);
      if (editingId === id) {
        setEditingId('');
        setDraft(EMPTY);
      }
      await load();
    } catch (err) {
      setError(contactAgentError(err));
    }
  };

  return (
    <Card className='rounded-2xl shadow-sm'>
      <CardHeader>
        <CardTitle>Ruteo de campañas</CardTitle>
      </CardHeader>
      <CardContent className='space-y-4'>
        <p className='text-sm text-muted-foreground'>
          Vinculá IDs externos de Meta/Google con un route del funnel. Gana el match más específico:
          anuncio, grupo/ad set, formulario y campaña.
        </p>
        {error ? <p className='text-sm text-destructive'>{error}</p> : null}

        <div className='grid gap-3 md:grid-cols-2 lg:grid-cols-3'>
          <div>
            <Label>Fuente</Label>
            <Input value={draft.source} onChange={event => setDraft(v => ({ ...v, source: event.target.value }))} placeholder='META / GOOGLE' />
          </div>
          <div>
            <Label>Route</Label>
            <select
              className='h-10 w-full rounded-md border bg-background px-3 text-sm'
              value={draft.routeKey}
              onChange={event => setDraft(v => ({ ...v, routeKey: event.target.value }))}
            >
              <option value=''>Seleccionar…</option>
              {availableRoutes.map(route => (
                <option key={route.key} value={route.key}>{route.key} → {route.vertical}</option>
              ))}
            </select>
          </div>
          <div><Label>Campaign ID</Label><Input value={draft.externalCampaignId} onChange={event => setDraft(v => ({ ...v, externalCampaignId: event.target.value }))} /></div>
          <div><Label>Ad set / grupo ID</Label><Input value={draft.externalGroupId} onChange={event => setDraft(v => ({ ...v, externalGroupId: event.target.value }))} /></div>
          <div><Label>Ad ID</Label><Input value={draft.externalAdId} onChange={event => setDraft(v => ({ ...v, externalAdId: event.target.value }))} /></div>
          <div><Label>Form ID</Label><Input value={draft.externalFormId} onChange={event => setDraft(v => ({ ...v, externalFormId: event.target.value }))} /></div>
          <div className='md:col-span-2'><Label>Nota</Label><Input value={draft.note} onChange={event => setDraft(v => ({ ...v, note: event.target.value }))} /></div>
          <label className='flex items-center gap-2 self-end pb-2 text-sm'>
            <input type='checkbox' checked={draft.enabled} onChange={event => setDraft(v => ({ ...v, enabled: event.target.checked }))} />
            Habilitado
          </label>
        </div>
        <div className='flex gap-2'>
          <Button type='button' onClick={() => void save()} disabled={saving || availableRoutes.length === 0}>
            {saving ? 'Guardando…' : editingId ? 'Guardar cambios' : 'Agregar mapping'}
          </Button>
          {editingId ? (
            <Button type='button' variant='outline' onClick={() => { setEditingId(''); setDraft(EMPTY); }}>
              Cancelar
            </Button>
          ) : null}
        </div>

        <div className='space-y-2'>
          {rows.length === 0 ? (
            <p className='text-sm text-muted-foreground'>Todavía no hay mappings externos. Los links con funnel_route funcionan igual sin esta tabla.</p>
          ) : rows.map(row => (
            <div key={row.id} className='flex flex-col gap-2 rounded-xl border p-3 text-sm md:flex-row md:items-center md:justify-between'>
              <div>
                <p className='font-medium'>{row.source} · {row.routeKey} → {routeLabels.get(row.routeKey) ?? 'route no disponible'}</p>
                <p className='text-xs text-muted-foreground'>
                  {[row.externalCampaignId && `campaign ${row.externalCampaignId}`, row.externalGroupId && `group ${row.externalGroupId}`, row.externalAdId && `ad ${row.externalAdId}`, row.externalFormId && `form ${row.externalFormId}`].filter(Boolean).join(' · ') || 'Cualquier interacción de esa fuente'}
                  {' · '}{row.enabled ? 'ON' : 'OFF'}
                </p>
              </div>
              <div className='flex gap-2'>
                <Button type='button' size='sm' variant='outline' onClick={() => edit(row)}>Editar</Button>
                <Button type='button' size='sm' variant='outline' onClick={() => void remove(row.id)}>Eliminar</Button>
              </div>
            </div>
          ))}
        </div>

        <div className='border-t pt-4'>
          <p className='mb-2 font-medium'>Resultados atribuidos</p>
          {analytics.length === 0 ? (
            <p className='text-sm text-muted-foreground'>Todavía no hay leads con atribución de campaña.</p>
          ) : (
            <div className='overflow-x-auto'>
              <table className='w-full text-left text-sm'>
                <thead className='text-xs text-muted-foreground'>
                  <tr>
                    <th className='py-2 pr-3'>Origen</th>
                    <th className='py-2 pr-3'>Campaña / anuncio</th>
                    <th className='py-2 pr-3'>Route / vertical</th>
                    <th className='py-2 pr-3 text-right'>Leads</th>
                    <th className='py-2 pr-3 text-right'>Calificados</th>
                    <th className='py-2 text-right'>Derivados</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.map((row, index) => (
                    <tr key={`${row.source}-${row.campaignId ?? ''}-${row.adId ?? ''}-${row.vertical ?? ''}-${index}`} className='border-t'>
                      <td className='py-2 pr-3'>{row.source}</td>
                      <td className='py-2 pr-3'>{row.campaignName ?? row.campaignId ?? '—'}{row.adName || row.adId ? ` · ${row.adName ?? row.adId}` : ''}</td>
                      <td className='py-2 pr-3'>{row.funnelRoute ?? '—'} · {row.vertical ?? '—'}</td>
                      <td className='py-2 pr-3 text-right'>{row.leads}</td>
                      <td className='py-2 pr-3 text-right'>{row.qualified}</td>
                      <td className='py-2 text-right'>{row.handedOff}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className='mt-2 text-xs text-muted-foreground'>
            Los costos (CPL/CAC) se podrán calcular cuando se incorpore spend desde Meta/Google o una carga de costos.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
